import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, type InferType, ref } from "yup";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, Button, Input, PhoneInput, PasswordInput } from "@envirohub/ui";
import { AlertCircle, X } from "lucide-react";
import { clearPasswordInputs } from "@envirohub/utils";
import { useRegister } from "@/api";
import { useAuthError } from "@/hooks/useAuthError";

const schema = object({
  firstName: string().required("validation.firstNameMin").min(2, "validation.firstNameMin"),
  lastName: string().required("validation.lastNameMin").min(2, "validation.lastNameMin"),
  email: string().required("validation.emailRequired").email("validation.invalidEmail"),
  phone: string().required("validation.phoneMin").min(8, "validation.phoneMin"),
  password: string()
    .required("validation.passwordRequired")
    .min(8, "validation.passwordRequirements")
    .matches(/[A-Z]/, "validation.passwordRequirements")
    .matches(/[a-z]/, "validation.passwordRequirements")
    .matches(/[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/, "validation.passwordRequirements"),
  confirmPassword: string()
    .oneOf([ref("password")], "validation.passwordsMatch")
    .required("validation.passwordsMatch"),
});

type FormData = InferType<typeof schema>;

export default function RegisterPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const resolveAuthError = useAuthError();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const registerMutation = useRegister();

  const formFields: ReadonlyArray<keyof FormData> = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "password",
    "confirmPassword",
  ];

  const passwordRequirementLabels = {
    length: t("validation.passwordLength"),
    upper: t("validation.passwordUpper"),
    lower: t("validation.passwordLower"),
    numberOrSpecial: t("validation.passwordNumberOrSpecial"),
  };

  const onSubmit = (data: FormData) => {
    setServerError(null);
    const { confirmPassword, ...payload } = data;
    registerMutation.mutate(
      { ...payload, preferredLanguage: i18n.language },
      {
        onSuccess: () => {
          navigate("/verify", { state: { contact: data.email, redirectTo: "/login" } });
        },
        onError: (error) => {
          const action = resolveAuthError(error);
          // EMAIL_ALREADY_EXISTS + VALIDATION_ERROR -> inline field errors.
          const fieldKeys = Object.keys(action.fieldErrors);
          let mappedAny = false;
          fieldKeys.forEach((key) => {
            if ((formFields as string[]).includes(key)) {
              setError(key as keyof FormData, {
                message: action.fieldErrors[key],
              });
              mappedAny = true;
            }
          });

          // Anything not tied to a field -> form-level alert.
          if (!mappedAny) setServerError(action.message);
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center">
        <h1 className="text-h3 font-bold text-surface-on dark:text-white">{t("auth.register")}</h1>
        <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] mt-1">
          {t("auth.createAccountDesc", {
            defaultValue: "Create your EnviroHub account",
          })}
        </p>
      </div>

      <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        {serverError && (
          <Alert variant="destructive" className="ps-10">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{serverError}</AlertDescription>
            <button
              type="button"
              onClick={() => setServerError(null)}
              className="absolute end-3 top-3 rounded-sm opacity-70 transition-opacity hover:opacity-100"
              aria-label={t("common.close")}
            >
              <X className="h-4 w-4" />
            </button>
          </Alert>
        )}
        {/* Name Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            id="firstName"
            type="text"
            autoComplete="given-name"
            label={t("auth.firstName")}
            placeholder={t("auth.firstNamePlaceholder")}
            error={errors.firstName?.message ? t(errors.firstName?.message) : undefined}
            required
            {...register("firstName")}
          />
          <Input
            id="lastName"
            type="text"
            autoComplete="family-name"
            label={t("auth.lastName")}
            placeholder={t("auth.lastNamePlaceholder")}
            error={errors.lastName?.message ? t(errors.lastName?.message) : undefined}
            required
            {...register("lastName")}
          />
        </div>

        {/* Email Field */}
        <Input
          id="email"
          type="email"
          autoComplete="username"
          label={t("auth.email")}
          placeholder={t("auth.emailPlaceholder")}
          error={errors.email?.message ? t(errors.email?.message) : undefined}
          required
          {...register("email")}
        />

        {/* Phone Field */}
        <PhoneInput
          name="phone"
          control={control}
          id="phone"
          autoComplete="tel"
          label={t("auth.phone")}
          placeholder={t("auth.phonePlaceholder")}
          error={errors.phone?.message ? t(errors.phone?.message) : undefined}
          required
        />

        <PasswordInput
          id="password"
          autoComplete="new-password"
          label={t("auth.password")}
          placeholder={t("auth.passwordPlaceholder")}
          error={errors.password?.message ? t(errors.password?.message) : undefined}
          showStrengthRequirements
          requirementLabels={passwordRequirementLabels}
          required
          {...register("password")}
        />

        {/* Confirm Password Field */}
        <PasswordInput
          id="confirmPassword"
          autoComplete="new-password"
          label={t("auth.confirmPassword")}
          placeholder={t("auth.confirmPasswordPlaceholder")}
          error={errors.confirmPassword?.message ? t(errors.confirmPassword?.message) : undefined}
          required
          {...register("confirmPassword")}
        />

        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={registerMutation.isPending}
          className="w-full mt-4 shadow-lg shadow-primary/25 hover:shadow-primary/40 transition-shadow"
        >
          {t("auth.register")}
        </Button>
      </form>

      <Link
        to="/register/organization"
        onClick={() => clearPasswordInputs()}
        className="text-primary font-bold no-underline hover:underline text-center"
      >
        {t("auth.isB2B")}
      </Link>

      <p className="text-body text-surface-on-variant dark:text-[#C3C9B4] text-center mt-2">
        {t("auth.alreadyHave")}{" "}
        <Link
          to="/login"
          onClick={() => clearPasswordInputs()}
          className="text-primary font-bold no-underline hover:underline transition-all"
        >
          {t("auth.login")}
        </Link>
      </p>
    </div>
  );
}
